import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SessionSummary } from '@teentack/shared';
import { apiFetch } from '@/lib/api-client';
import { Card, CardHeading } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';

/** Device list with remote logout. [FR-M01.6] */
export function SessionsCard() {
  const queryClient = useQueryClient();

  const { data, isPending, error } = useQuery({
    queryKey: ['sessions'],
    queryFn: () => apiFetch<{ sessions: SessionSummary[] }>('/auth/sessions'),
  });

  const revoke = useMutation({
    mutationFn: (sessionId: string) =>
      apiFetch<void>(`/auth/sessions/${sessionId}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sessions'] }),
  });

  return (
    <Card as="section">
      <CardHeading>Signed-in devices</CardHeading>

      {isPending && <p className="mt-4 text-slate-500">Loading…</p>}
      {error && <p className="mt-4 text-sm text-red-600">{error.message}</p>}

      <ul className="mt-2 divide-y divide-line">
        {data?.sessions.map((session) => (
          <li key={session.id} className="flex items-center justify-between py-3">
            <div>
              <p className="text-sm font-semibold text-ink">
                {session.deviceInfo ?? 'Unknown device'}
                {session.current && (
                  <span className="ml-2 rounded-full bg-safe-50 px-2 py-0.5 text-xs font-semibold text-safe-700 ring-1 ring-safe-200 ring-inset">
                    this device
                  </span>
                )}
              </p>
              <p className="text-xs text-slate-500">
                {session.ipAddress ?? 'no address'} · since{' '}
                {new Date(session.issuedAt).toLocaleString()}
              </p>
            </div>
            {!session.current && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => revoke.mutate(session.id)}
                disabled={revoke.isPending}
              >
                Sign out
              </Button>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}
